import ExcelJS from 'exceljs';
import { taskIdString } from './taskDependencyDetail';

function getExcelColumnLetter(colNum) {
  let temp, letter = '';
  while (colNum > 0) {
    temp = (colNum - 1) % 26;
    letter = String.fromCharCode(temp + 65) + letter;
    colNum = (colNum - temp - 1) / 26;
  }
  return letter;
}

/**
 * Resolve a workbook sheet name that does not collide with existing sheets.
 * Excel sheet names are case-insensitive and max 31 chars.
 *
 * @param {import('exceljs').Workbook} workbook
 * @param {string} desiredName
 * @returns {string}
 */
function uniqueWorksheetName(workbook, desiredName = 'Instruction') {
  const base = String(desiredName || 'Instruction').trim() || 'Instruction';
  const existing = new Set(
    (workbook?.worksheets || []).map((ws) => String(ws.name || '').toLowerCase())
  );

  const clamp = (name) => String(name).slice(0, 31);
  if (!existing.has(base.toLowerCase())) return clamp(base);

  for (let n = 2; n < 1000; n += 1) {
    const suffix = ` (${n})`;
    const candidate = clamp(`${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`);
    if (!existing.has(candidate.toLowerCase())) return candidate;
  }

  return clamp(`${base.slice(0, 24)}_${Date.now().toString().slice(-6)}`);
}

/**
 * Add numbered instruction rows worksheet (matches bulk checklist template layout).
 *
 * @param {import('exceljs').Workbook} workbook
 * @param {string[]} instructions
 * @param {string} [sheetName]
 */
export function addTasklistInstructionSheet(
  workbook,
  instructions = [],
  sheetName = 'Instruction'
) {
  if (!workbook || !Array.isArray(instructions) || !instructions.length) return;

  const worksheet = workbook.addWorksheet(uniqueWorksheetName(workbook, sheetName));
  instructions.forEach((text, index) => {
    const row = worksheet.addRow([String(index + 1), text]);
    row.getCell(2).alignment = { wrapText: true, vertical: 'top' };
  });

  worksheet.getColumn(1).width = 6;
  worksheet.getColumn(2).width = 120;
}

export async function exportTasklistExcel({
  title = "Export",
  subtitle = "",
  dateRange = "",
  taskItems = [],
  columns = [],
  getRowPrefix = () => undefined,
  filename = "export.xlsx",
  sheetName = "Sheet 1",
  instructions = [],
  instructionsSheetName = 'Instruction',
}) {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    let currentRow = 1;
    const lastColLetter = getExcelColumnLetter(1 + columns.length);

    // Title
  worksheet.mergeCells(`B${currentRow}:${lastColLetter}${currentRow}`);
    const titleCell = worksheet.getCell(`B${currentRow}`);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 20 };
    currentRow++;

    // Subtitle
    if (subtitle) {
      worksheet.mergeCells(`B${currentRow}:${lastColLetter}${currentRow}`);
      const subtitleCell = worksheet.getCell(`B${currentRow}`);
      subtitleCell.value = subtitle;
      subtitleCell.font = { bold: true, size: 14 };
      currentRow++;
    }

    // Date range
    if (dateRange) {
      worksheet.mergeCells(`B${currentRow}:${lastColLetter}${currentRow}`);
      const dateCell = worksheet.getCell(`B${currentRow}`);
      dateCell.value = dateRange;
      dateCell.font = { bold: true, size: 14 };
      currentRow++;

      // ✅ Add one blank row after date range
      worksheet.addRow([]);
      currentRow++;
    }

    // Header Row
    const headerRowData = [undefined, ...columns.map(col => {
      if (typeof col.header === 'object' && col.header.v) {
        return col.header.v;
      }
      return col.header;
    })];
    worksheet.addRow(headerRowData);
    const headerRow = worksheet.getRow(currentRow);

    // Apply header styles
    columns.forEach((col, index) => {
      const cell = headerRow.getCell(index + 2); // +2 due to prefix
      if (col.header && typeof col.header === 'object' && col.header.s) {
        cell.font = col.header.s.font || { bold: true };
        cell.alignment = col.header.s.alignment || { vertical: 'middle', horizontal: 'left' };
      } else {
        cell.font = { bold: true };
      }
    });

    currentRow++;

    // Optional: Dynamic dropdown mapping for known fields
    const dropdownMap = {
      priority: '"High,Medium,Low"',
      status: '"Open,In Progress,Closed"',
    };

    // Data Rows
    for (const task of taskItems) {
      const rowData = [getRowPrefix(task)];

      for (const col of columns) {
        let value;

        if (typeof col.value === 'function') {
          const result = col.value(task);
          if (typeof result === 'object' && result instanceof Date) {
            value = result;
          } else if (typeof result === 'object' && result.v) {
            value = result.v;
          } else {
            value = result;
          }
        } else if (typeof col.value === 'string') {
          value = task[col.value];
        } else {
          value = "";
        }

        rowData.push(value);
      }

      const addedRow = worksheet.addRow(rowData);

      // Apply styles and validation to each cell
      columns.forEach((col, index) => {
        const cell = addedRow.getCell(index + 2); // +2 due to prefix
        const val = cell.value;

        // Date formatting
        if (val instanceof Date) {
          cell.numFmt = 'dd-mmm-yyyy';
        }

        // Dropdown validation for dynamic fields
        const key = col.header?.v?.toLowerCase();
        if (dropdownMap[key]) {
          cell.dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [dropdownMap[key]],
          };
        }

        // Normal font for data cells
        cell.font = { bold: false };

        if (col.wrapText) {
          cell.alignment = {
            ...(cell.alignment || {}),
            wrapText: true,
            vertical: 'top',
          };
        }
      });

      currentRow++;
    }

    // Auto-fit column widths (optional per-column maxWidth / minWidth)
    worksheet.columns.forEach((column, colIndex) => {
      if (colIndex === 0) {
        column.width = 4;
        return;
      }

      const colDef = columns[colIndex - 1];
      let maxLength = 10;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const val = cell.value ? cell.value.toString() : '';
        const lines = val.split(/\r?\n/);
        const longestLine = lines.reduce(
          (max, line) => Math.max(max, line.length),
          0
        );
        maxLength = Math.max(maxLength, longestLine);
      });

      let width = maxLength + 2;
      if (colDef?.maxWidth != null) {
        width = Math.min(width, colDef.maxWidth);
      }
      if (colDef?.minWidth != null) {
        width = Math.max(width, colDef.minWidth);
      }
      column.width = width;
    });

    addTasklistInstructionSheet(workbook, instructions, instructionsSheetName);

    // Create and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Excel Download Error:', error);
    throw error instanceof Error
      ? error
      : new Error(String(error?.message || error || 'Failed to export Excel file'));
  }
}

/**
 * Adds an "Inactive" label to users who are not present in the active user list.
 *
 * For each user in `userList`, if their `_id` is not included in `activeUserIds`,
 * the function appends " (Inactive)" to their `name` and `displayname` fields,
 * and sets an `inactive` property to true.
 *
 * @param {Array<Object>} userList - Array of user objects to process.
 * @param {Array<string>} activeUserIds - Array of active user IDs.
 * @returns {Array<Object>} The updated user list with inactive labels applied where appropriate.
 */
export function attachInactiveLabel(userList, activeUserIds) {
  return userList?.map(user => {
    const userId = user?._id || user?.id || null;
    if (!activeUserIds?.includes(userId)) {
      return { ...user, name: `${user.name || user.displayname} (Inactive)`, inactive: true };
    }
    return user;
  });
}

/**
 * Parses URL query parameters and converts them to filter objects.
 * Supports multiple values, operators (gt, gte, lt, lte), and validation.
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.query - URL query parameters from route
 * @param {Array<string>} config.standardParams - Parameter names to exclude from filtering (e.g., ['cid'])
 * @param {Object} config.keyMap - Mapping from URL param names to internal column names
 * @param {Array<Object>} config.locationList - List of valid locations for validation
 * @param {Array<Object>} config.usersList - List of valid users for validation
 * @param {Array<Object>} config.tableHeadersfilter - Table headers with filterable flag
 * @param {Array<Object>} config.masterTaskList - Full task list for operator-based filtering
 * @returns {Object|null} Filter object ready to apply, or null if no valid filters
 */
export function parseUrlQueryFilters({
  query,
  standardParams = ['cid', 'view', 'userType'],
  keyMap = {
    location: 'locationName',
    status: 'stage1',
    sub_category: 'subcategory',
    due_in_days: 'duedays',
    preparer: 'preparerids',
    approver: 'approverids',
  },
  locationList = [],
  usersList = [],
  tableHeadersfilter = [],
  masterTaskList = [],
}) {
  try {
    // 1. Extract only filter-related query params
    const filterParams = Object.keys(query).filter(
      key => !standardParams.includes(key) && query[key]
    );

    if (filterParams.length === 0) {
      console.log('No filter parameters found in URL');
      return null;
    }

    console.log('Applying filters from URL:', query);

    const urlFilters = {};

    // Helper: safely decode and clean parameter values
    const decodeAndClean = val => {
      try {
        let decoded = decodeURIComponent(val);
        // Remove surrounding quotes if present (handles both single and double quotes)
        decoded = decoded.replace(/^["']|["']$/g, '');
        return decoded;
      } catch (err) {
        console.warn(`Failed to decode URL parameter value "${val}":`, err);
        // If decoding fails, just remove quotes from original value
        return val.replace(/^["']|["']$/g, '');
      }
    };

    // 2. Iterate through URL params
    for (const key of filterParams) {
      // Check if key has operators first (e.g., due_in_days[gte])
      const operatorMatch = key.match(/^(.+)\[(gt|gte|lt|lte)\]$/);
      const baseKey = operatorMatch ? operatorMatch[1] : key;
      
      // Map the base key (without operator) to internal column name
      const mappedKey = keyMap[baseKey] || baseKey;
      
      // Get all values for this parameter (handles both single and multiple values)
      let rawValues = query[key];

      // Ensure rawValues is always an array
      if (!Array.isArray(rawValues)) {
        rawValues = [rawValues];
      }
      
      // Decode and clean all values (remove quotes, decode URI)
      const decodedValues = rawValues.map(val => decodeAndClean(val)).filter(Boolean);

      if (decodedValues.length === 0) {
        console.warn(`No valid values for ${key}`);
        continue;
      }

      // Special: location check - validate all location values
      if (key === 'location') {
        const validLocations = decodedValues.filter(locName => {
          const found = locationList.find(loc => loc.name === locName);
          if (!found) {
            console.warn(`Location "${locName}" not found`);
          }
          return found;
        });
        
        if (validLocations.length === 0) {
          console.warn(`No valid locations found for values: ${decodedValues.join(', ')}`);
          continue;
        }
        
        // Use only valid locations
        decodedValues.length = 0;
        decodedValues.push(...validLocations);
      }

      // Special: preparer/approver check - convert to exact user names as stored
      if (key === 'preparer' || key === 'approver') {
        const validNames = decodedValues.map(nameOrEmail => {
          // Try to find by name first, then by email (case-insensitive)
          const user = usersList.find(u => 
            u.name?.toLowerCase() === nameOrEmail.toLowerCase() || 
            u.email?.toLowerCase() === nameOrEmail.toLowerCase()
          );
          if (!user) {
            console.warn(`User "${nameOrEmail}" not found for ${key}`);
            return null;
          }
          // Return the exact name as stored (for matching with getNamesByIds output)
          return user.name;
        }).filter(Boolean);
        
        if (validNames.length === 0) {
          console.warn(`No valid users found for ${key}: ${decodedValues.join(', ')}`);
          continue;
        }
        
        // Use exact user names for filtering
        decodedValues.length = 0;
        decodedValues.push(...validNames);
      }

      // Validate filterable header
      const header = tableHeadersfilter.find(
        h => h.value === mappedKey && h.filterable
      );

      if (!header) {
        console.warn(`Column '${mappedKey}' not filterable or missing`);
        continue;
      }

      // Type-based handling
      if (mappedKey === 'duedays') {
        // Handle comparison operators for duedays
        const operatorMatch = key.match(/\[(gt|gte|lt|lte)\]$/);
        
        if (operatorMatch) {
          const operator = operatorMatch[1]; // 'gt', 'gte', 'lt', or 'lte'
          const numVal = Number(decodedValues[0]);
          console.log(operator, numVal, "Operator filter detected");
          
          if (!isNaN(numVal)) {
            // Filter masterTaskList based on operator
            const filteredTasks = masterTaskList.filter(task => {
              const taskDueDays = task.duedays;
              if (taskDueDays === undefined || taskDueDays === null) return false;
              
              switch (operator) {
                case 'gte': return taskDueDays >= numVal;
                case 'gt': return taskDueDays > numVal;
                case 'lte': return taskDueDays <= numVal;
                case 'lt': return taskDueDays < numVal;
                default: return false;
              }
            });
            
            // Extract the duedays values that match the criteria
            const matchingDueDays = [...new Set(filteredTasks.map(t => t.duedays))];
            
            console.log(`Filtered ${filteredTasks.length} tasks with duedays ${operator} ${numVal}`);
            console.log(`Matching duedays values:`, matchingDueDays);
            
            if (matchingDueDays.length > 0) {
              urlFilters[mappedKey] = matchingDueDays;
              console.log(`Added filter: ${key} → ${mappedKey} = [${matchingDueDays.join(', ')}]`);
            } else {
              console.warn(`No tasks found with duedays ${operator} ${numVal}`);
            }
          } else {
            console.warn(`Invalid number for ${key}: ${decodedValues[0]}`);
          }
          continue; // Skip normal processing
        } else {
          // Regular equality check (exact match or array of values)
          const numVals = decodedValues
            .map(val => Number(val))
            .filter(num => !isNaN(num));
          
          if (numVals.length > 0) {
            urlFilters[mappedKey] = numVals;
          } else {
            console.warn(`Invalid numbers for ${mappedKey}: ${decodedValues.join(', ')}`);
          }
        }
      } else {
        // Store all clean values as array
        urlFilters[mappedKey] = decodedValues;
      }

      console.log(`Added filter: ${key} → ${mappedKey} =`, urlFilters[mappedKey]);
    }

    // 3. Return filters if any
    if (!Object.keys(urlFilters).length) {
      console.log('No valid filters to apply');
      return null;
    }

    console.log('Final filter object:', urlFilters);
    return urlFilters;

  } catch (err) {
    console.error('Error parsing URL filters:', err);
    return null;
  }
}

/**
 * Validates a due-day input value.
 * Returns an error message string if invalid, or empty string if valid.
 * @param {*} val - The input value to validate
 * @param {number} maxDays - Maximum allowed absolute value (default: MAX_DUE_DATE_DAYS)
 * @returns {string} Error message or empty string
 */
export function validateDueDays(val, maxDays = 200) {
  if (val === null || val === undefined || val === '') {
    return ''; // Empty is allowed (not required field)
  }

  const duedayStr = String(val).trim();

  // Check for alphabetic characters
  if (/[a-zA-Z]/.test(duedayStr)) {
    return 'Please provide a number values only';
  }

  // Check for invalid patterns like "0-0", "0-300", "1-2" (range patterns)
  if (/^\d+-\d+$/.test(duedayStr)) {
    return 'Please give correct validated values';
  }

  // Check if it's a valid integer (allows negative sign at start only)
  if (!/^-?\d+$/.test(duedayStr)) {
    return 'Please give correct validated values';
  }

  // Check range -maxDays to maxDays
  const dueday = Number(duedayStr);
  if (dueday < -maxDays || dueday > maxDays) {
    return `Enter value between -${maxDays} and ${maxDays}`;
  }

  return '';
}

const TASKLIST_USER_TYPE_STORAGE_PREFIX = 'tasklist-userType:';

/** Read a query param from the live address bar (replaceState does not update $route). */
export function getQueryParamFromLocation(param) {
  if (typeof window === 'undefined' || !param) return null;
  return new URLSearchParams(window.location.search).get(param);
}

/** sessionStorage key for persisting persona across full page refresh (per checklist). */
export function getTasklistUserTypeStorageKey(checklistId) {
  return checklistId ? `${TASKLIST_USER_TYPE_STORAGE_PREFIX}${checklistId}` : null;
}

export function readPersistedTasklistUserType(checklistId) {
  const key = getTasklistUserTypeStorageKey(checklistId);
  if (!key) return null;
  try {
    return sessionStorage.getItem(key);
  } catch (err) {
    console.log('[tasklistUtils]: readPersistedTasklistUserType', err);
    return null;
  }
}

export function persistTasklistUserType(checklistId, userType) {
  const key = getTasklistUserTypeStorageKey(checklistId);
  if (!key || !userType) return;
  try {
    sessionStorage.setItem(key, userType);
  } catch (err) {
    console.log('[tasklistUtils]: persistTasklistUserType', err);
  }
}

/** Drop the per-cycle persona so Home / cycle switch can default to Manager. */
export function clearPersistedTasklistUserType(checklistId) {
  const key = getTasklistUserTypeStorageKey(checklistId);
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch (err) {
    console.log('[tasklistUtils]: clearPersistedTasklistUserType', err);
  }
}

/**
 * Resolve the active tasklist persona (manager / preparer / approver).
 * Prefers in-session store/component state over URL, because syncUserTypeQueryParam
 * uses history.replaceState and leaves Vue Router $route.query stale.
 */
export function resolveTasklistUserType({
  preferred,
  route,
  store,
  savedListState,
  availableRoles,
  defaultRole = 'manager',
}) {
  const checklistId = route?.params?.id;
  const candidates = [
    preferred,
    store?.state?.tasklistUserType,
    readPersistedTasklistUserType(checklistId),
    savedListState?.userType,
    getQueryParamFromLocation('userType'),
    route?.query?.userType,
  ];
  for (const role of candidates) {
    if (role && availableRoles.includes(role)) {
      return role;
    }
  }
  if (defaultRole && availableRoles.includes(defaultRole)) {
    return defaultRole;
  }
  return availableRoles[0] || 'manager';
}

const TASKLIST_FORCE_REFRESH_PREFIXES = [
  '/financialclose/project/trialbalance/',
  '/financialclose/project/informationrequests/',
  '/financialclose/project/dashboard/',
];

/**
 * Whether the tasklist page should force-refresh APIs on enter.
 * @param {{ path?: string }|null|undefined} fromRoute - vue-router `from` route
 * @returns {boolean}
 */
export function shouldForceTasklistRefresh(fromRoute) {
  const fromPath = fromRoute?.path || '';
  if (!fromPath) return true;

  if (
    fromPath.includes('/financialclose/project/checklist/')
    && fromPath.includes('/task/')
  ) {
    return false;
  }

  if (TASKLIST_FORCE_REFRESH_PREFIXES.some((prefix) => fromPath.includes(prefix))) {
    return true;
  }

  if (
    fromPath.includes('/financialclose/project/checklist/')
    && !fromPath.includes('/task/')
  ) {
    return false;
  }

  return true;
}

/**
 * Task already in Vuex from list cache or a create-success toast.
 * Do not require the row to be in checklist — getAllTasks may still be in flight.
 */
export function getCachedTaskForDetail(store, projectId, taskId) {
  const wantedId = taskIdString(taskId);
  const wantedProject = taskIdString(projectId);
  if (!wantedId || !store) return null;

  const cached = store.getters['tasks/getTaskById']?.(wantedId)
    || store.getters['tasks/getCurrentTask'];
  if (cached && taskIdString(cached._id) === wantedId) {
    const cachedProject = taskIdString(cached.project);
    if (!cachedProject || !wantedProject || cachedProject === wantedProject) {
      return cached;
    }
  }

  const data = store.getters['tasks/getTaskListData'];
  if (taskIdString(data?.metadata?._id) !== wantedProject || !Array.isArray(data?.checklist)) {
    return null;
  }
  return data.checklist.find((row) => row && taskIdString(row._id) === wantedId) || null;
}

/**
 * Open the task detail page using the same flow as Tasklist row click.
 * Caches the task, persists list/horizon view for back navigation, then router.push.
 * @param {string} [userType] Active persona selection from the caller; falls back to current route query/store when omitted.
 */
export function openTaskDetailPage({
  router,
  store,
  task,
  checklistId,
  userType,
  tasklistView = null,
}) {
  const taskId = taskIdString(task?._id);
  if (!taskId || !router || !store) return;

  const resolvedChecklistId = taskIdString(
    checklistId || task.project || router.currentRoute?.params?.id,
  );
  const fullTask = {
    ...(store.getters['tasks/getTaskById']?.(taskId) || task),
    _id: taskId,
    project: resolvedChecklistId || task.project,
  };

  store.commit('tasks/CACHE_TASK', fullTask);
  store.commit('tasks/SET_CURRENT_TASK', fullTask);

  if (resolvedChecklistId && taskId) {
    store.dispatch('tasks/hydrateTaskDependencyDetail', {
      projectId: resolvedChecklistId,
      taskId,
    }).catch((err) => {
      console.log('[openTaskDetailPage]: hydrateTaskDependencyDetail', err?.message || err);
    });
  }

  if (tasklistView && resolvedChecklistId) {
    store.commit('tasks/SET_TASKLIST_VIEW', {
      checklistId: resolvedChecklistId,
      view: tasklistView,
    });
  }

  const availableRoles = ['manager', 'preparer', 'approver'];
  const resolvedUserType = resolveTasklistUserType({
    preferred: userType,
    route: router.currentRoute,
    store,
    availableRoles,
    defaultRole: 'manager',
  });

  const projectId = taskIdString(fullTask.project) || resolvedChecklistId;
  const targetPath = `/financialclose/project/checklist/${projectId}/task/${taskId}`;

  if (router.currentRoute?.path !== targetPath) {
    router.push({
      path: targetPath,
      query: {
        cid: router.currentRoute?.query?.cid || store.state.client,
        userType: resolvedUserType,
      },
    }).catch((err) => {
      if (err?.name !== 'NavigationDuplicated') {
        console.log('[openTaskDetailPage]: router.push', err?.message || err);
      }
    });
  }
}